import React from 'react';
import {
    Heading1, Heading2, Heading3, List,
    CheckSquare, Quote, ChevronRight,
    Info, AlertTriangle, Lightbulb, ShieldAlert,
    Trash2, Copy, Columns as ColumnsIcon
} from 'lucide-react';

export default function BlockActionMenu({ editor, activeBlock, position, onClose, forwardRef }) {
    if (!editor || !activeBlock || !activeBlock.node || !position) return null;

    const handleBlockAction = (action, payload = null) => {
        const { node, pos } = activeBlock;
        editor.chain().focus().setNodeSelection(pos).run();

        if (action === 'delete') {
            editor.chain().focus().deleteSelection().run();
        } else if (action === 'duplicate') {
            const content = node.toJSON();
            const insertPos = pos + node.nodeSize;
            editor.chain().focus().insertContentAt(insertPos, content).run();
        } else if (action === 'setColumns') {
            editor.chain().focus().updateAttributes('columns', { count: payload }).run();
        } else if (action === 'turnInto') {
            if (payload === 'h1') editor.chain().focus().setHeading({ level: 1 }).run();
            if (payload === 'h2') editor.chain().focus().setHeading({ level: 2 }).run();
            if (payload === 'h3') editor.chain().focus().setHeading({ level: 3 }).run();
            if (payload === 'bulletList') editor.chain().focus().toggleBulletList().run();
            if (payload === 'taskList') editor.chain().focus().toggleTaskList().run();
            if (payload === 'quote') editor.chain().focus().toggleBlockquote().run();
            if (payload === 'details') editor.chain().focus().setDetails().run();
            if (payload === 'calloutInfo') editor.chain().focus().setCallout({ type: 'info' }).run();
            if (payload === 'calloutWarning') editor.chain().focus().setCallout({ type: 'warning' }).run();
            if (payload === 'calloutTip') editor.chain().focus().setCallout({ type: 'tip' }).run();
            if (payload === 'calloutDanger') editor.chain().focus().setCallout({ type: 'danger' }).run();
        }

        if (onClose) onClose();
    };

    return (
        <div
            ref={forwardRef}
            className="tiptap-slash tiptap-block-menu"
            style={{ top: position.top, left: position.left, zIndex: 60 }}
        >
            {activeBlock.node.type.name === 'columns' ? (
                <>
                    <div className="tiptap-slash__category">Layout Options</div>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('setColumns', 2)}>
                        <ColumnsIcon size={16} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">2 Columns</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('setColumns', 3)}>
                        <ColumnsIcon size={16} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">3 Columns</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('delete')}>
                        <Trash2 size={16} className="text-danger" style={{ color: 'var(--error)' }} />
                        <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Delete Layout</span></div>
                    </button>
                </>
            ) : (
                <>
                    <div className="tiptap-slash__category">Actions</div>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('delete')}>
                        <Trash2 size={16} className="text-danger" style={{ color: 'var(--error)' }} />
                        <div className="tiptap-slash__item-text">
                            <span className="tiptap-slash__item-label">Delete</span>
                        </div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('duplicate')}>
                        <Copy size={16} />
                        <div className="tiptap-slash__item-text">
                            <span className="tiptap-slash__item-label">Duplicate</span>
                        </div>
                    </button>

                    <div className="tiptap-slash__category" style={{ marginTop: 'var(--space-xs)' }}>Turn Into</div>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'h1')}>
                        <Heading1 size={16} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Heading 1</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'h2')}>
                        <Heading2 size={16} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Heading 2</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'h3')}>
                        <Heading3 size={16} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Heading 3</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'bulletList')}>
                        <List size={16} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Bullet List</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'taskList')}>
                        <CheckSquare size={16} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Task List</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'quote')}>
                        <Quote size={16} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Quote</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'details')}>
                        <ChevronRight size={16} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Toggle</span></div>
                    </button>
                    <div className="tiptap-slash__category" style={{ marginTop: 'var(--space-xs)' }}>Callouts</div>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'calloutInfo')}>
                        <Info size={16} style={{ color: 'var(--brand-primary)' }} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Info Box</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'calloutTip')}>
                        <Lightbulb size={16} style={{ color: 'var(--success)' }} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Tip Box</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'calloutWarning')}>
                        <AlertTriangle size={16} style={{ color: 'var(--warning)' }} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Warning</span></div>
                    </button>
                    <button className="tiptap-slash__item" onClick={() => handleBlockAction('turnInto', 'calloutDanger')}>
                        <ShieldAlert size={16} className="text-danger" style={{ color: 'var(--error)' }} /> <div className="tiptap-slash__item-text"><span className="tiptap-slash__item-label">Danger</span></div>
                    </button>
                </>
            )}
        </div>
    );
}
